import { Box, Typography, Button, Input, Card, Grid, Chip, Avatar, Select, Option, FormControl, FormLabel } from '@mui/joy';
import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import MetricCard from '../components/MetricCard';
import EnhancedButton from '../components/EnhancedButton';
import EnhancedInput from '../components/EnhancedInput';
import LoadingSkeleton, { CardSkeleton } from '../components/LoadingSkeleton';

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('name');
  const [filterBy, setFilterBy] = useState('all');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'

  useEffect(() => {
    const fetchClients = async () => {
      setLoading(true);
      const { data, error } = await supabase.from('clients').select('*, gis_projects(*), projects(*)');
      if (!error) {
        setClients(data || []);
      }
      setLoading(false);
    };
    fetchClients();
  }, []);

  // Helper functions
  const getClientInitials = (clientName) => {
    return clientName.split(' ').map(word => word[0]).join('').toUpperCase();
  };

  const getClientColor = (clientName) => {
    const colors = ['#00A884', '#007E33', '#17a2b8', '#6c757d', '#28a745', '#ffc107', '#dc3545'];
    const index = clientName.length % colors.length;
    return colors[index];
  };

  const getProjectStatusSummary = (projects) => {
    const active = projects.filter(p => p.status === 'active').length;
    const completed = projects.filter(p => p.status === 'completed').length;
    const pending = projects.length - active - completed;
    return { active, completed, pending, total: projects.length };
  };

  // Filter and sort logic
  let filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filterBy === 'all' || 
      (filterBy === 'active' && (client.projects?.length > 0)) ||
      (filterBy === 'inactive' && (client.projects?.length === 0));
    return matchesSearch && matchesFilter;
  });

  // Sort logic
  filteredClients.sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'projects':
        return (b.projects?.length || 0) - (a.projects?.length || 0);
      case 'gis_projects':
        return (b.gis_projects?.length || 0) - (a.gis_projects?.length || 0);
      default:
        return 0;
    }
  });

  // Analytics data
  const totalClients = clients.length;
  const activeClients = clients.filter(c => c.projects?.length > 0).length;
  const totalProjects = clients.reduce((sum, c) => sum + (c.projects?.length || 0), 0);
  const totalGISProjects = clients.reduce((sum, c) => sum + (c.gis_projects?.length || 0), 0);

  if (loading) {
    return (
      <PageLayout title="Clients" fullWidth>
        <Box sx={{ maxWidth: '1400px', mx: 'auto', p: 3 }}>
          {/* Analytics Loading */}
          <Box sx={{ mb: 4 }}>
            <LoadingSkeleton height="40px" width="200px" sx={{ mb: 3 }} />
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
              <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
              <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
              <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            </Grid>
          </Box>
          
          {/* Controls Loading */}
          <CardSkeleton />
          
          {/* Clients Loading */}
          <Box sx={{ mt: 4 }}>
            <Grid container spacing={3}>
              {[...Array(8)].map((_, i) => (
                <Grid key={i} xs={12} sm={6} md={4} lg={3}><CardSkeleton /></Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Clients" fullWidth>
      <Box sx={{ maxWidth: '1400px', mx: 'auto', p: 3 }}>
        {/* Analytics Overview */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            level="h3" 
            sx={{ 
              mb: 3, 
              color: 'success.700',
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
            className="fade-in"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '32px', fontFamily: 'Material Symbols Outlined' }}>
              business
            </span>
            Client Portfolio Overview
          </Typography>
          
          <Grid container spacing={3} sx={{ mb: 4 }} className="fade-in-up">
            <Grid xs={12} sm={6} md={3}>
              <MetricCard
                title="Total Clients"
                value={totalClients}
                icon="business"
                gradientColors={['#00A884', '#007E33']}
              />
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <MetricCard
                title="Active Clients"
                value={activeClients}
                icon="trending_up"
                gradientColors={['#28a745', '#218838']}
              />
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <MetricCard
                title="Total Projects"
                value={totalProjects}
                icon="folder"
                gradientColors={['#17a2b8', '#138496']}
              />
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <MetricCard
                title="GIS Projects"
                value={totalGISProjects}
                icon="map"
                gradientColors={['#6c757d', '#5a6268']}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Controls */}
        <Card 
          sx={{ 
            p: 3, 
            mb: 4,
            background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.02) 0%, rgba(0, 126, 51, 0.02) 100%)',
            border: '1px solid',
            borderColor: 'success.200',
          }}
          className="fade-in"
        >
          <Typography 
            level="h4" 
            sx={{ 
              mb: 3, 
              color: 'success.700',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
              tune
            </span>
            Search, Filter & View Options
          </Typography>
          
          <Grid container spacing={2} alignItems="flex-end">
            <Grid xs={12} sm={4} md={3}>
              <EnhancedInput
                placeholder="Search clients by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    search
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12} sm={3} md={2}>
              <FormControl>
                <FormLabel>Filter</FormLabel>
                <Select
                  value={filterBy}
                  onChange={(e, val) => setFilterBy(val)}
                  size="sm"
                  sx={{ '&:focus-within': { borderColor: 'success.500', boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)' }}}
                >
                  <Option value="all">All Clients</Option>
                  <Option value="active">Active Clients</Option>
                  <Option value="inactive">Inactive Clients</Option>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid xs={12} sm={3} md={2}>
              <FormControl>
                <FormLabel>Sort By</FormLabel>
                <Select
                  value={sortBy}
                  onChange={(e, val) => setSortBy(val)}
                  size="sm"
                  sx={{ '&:focus-within': { borderColor: 'success.500', boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)' }}}
                >
                  <Option value="name">Name</Option>
                  <Option value="projects">Project Count</Option>
                  <Option value="gis_projects">GIS Projects</Option>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid xs={12} sm={2} md={2}>
              <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
                <EnhancedButton
                  size="sm"
                  variant={viewMode === 'cards' ? 'solid' : 'outlined'}
                  onClick={() => setViewMode('cards')}
                  sx={{ minWidth: 'auto', px: 1 }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                    grid_view
                  </span>
                </EnhancedButton>
                <EnhancedButton
                  size="sm"
                  variant={viewMode === 'table' ? 'solid' : 'outlined'}
                  onClick={() => setViewMode('table')}
                  sx={{ minWidth: 'auto', px: 1 }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                    table_rows
                  </span>
                </EnhancedButton>
              </Box>
            </Grid>
          </Grid>
          
          {search && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'success.25', borderRadius: 'sm' }}>
              <Typography level="body-sm">
                Found {filteredClients.length} client{filteredClients.length !== 1 ? 's' : ''} matching "{search}"
              </Typography>
            </Box>
          )}
        </Card>

        {/* Client List */}
        <Box className="fade-in-up">
          {viewMode === 'cards' ? (
            <Grid container spacing={3}>
              {filteredClients.map((client) => {
                const projectStatus = getProjectStatusSummary(client.projects || []);
                
                return (
                  <Grid key={client.id} xs={12} sm={6} md={4} lg={3}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 25px rgba(0, 168, 132, 0.15)',
                        }
                      }}
                      component={Link}
                      to={`/clients/${client.id}`}
                    >
                      {/* Client Header */}
                      <Box 
                        sx={{ 
                          p: 2, 
                          background: `linear-gradient(135deg, ${getClientColor(client.name)}15 0%, ${getClientColor(client.name)}05 100%)`,
                          borderBottom: '1px solid',
                          borderColor: 'neutral.200',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar 
                            sx={{ 
                              bgcolor: getClientColor(client.name),
                              color: 'white',
                              fontWeight: 700,
                              fontSize: '1rem'
                            }}
                          >
                            {getClientInitials(client.name)}
                          </Avatar>
                          
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography 
                              level="title-sm" 
                              sx={{ 
                                color: 'success.700',
                                fontWeight: 600,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {client.name}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* Client Metrics */}
                      <Box sx={{ p: 2 }}>
                        <Grid container spacing={1} sx={{ mb: 2 }}>
                          <Grid xs={6}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography level="h4" sx={{ color: 'success.600' }}>
                                {projectStatus.total}
                              </Typography>
                              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                Projects
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid xs={6}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography level="h4" sx={{ color: 'info.600' }}>
                                {client.gis_projects?.length || 0}
                              </Typography>
                              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                GIS Maps
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>

                        {/* Status Chips */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                          {projectStatus.active > 0 && (
                            <Chip size="sm" variant="soft" color="success">
                              {projectStatus.active} Active
                            </Chip>
                          )}
                          {projectStatus.pending > 0 && (
                            <Chip size="sm" variant="soft" color="warning">
                              {projectStatus.pending} Pending
                            </Chip>
                          )}
                          {projectStatus.completed > 0 && (
                            <Chip size="sm" variant="soft" color="neutral">
                              {projectStatus.completed} Complete
                            </Chip>
                          )}
                          {projectStatus.total === 0 && (
                            <Chip size="sm" variant="soft" color="neutral">
                              No Projects
                            </Chip>
                          )}
                        </Box>

                        {/* Action Button */}
                        <EnhancedButton
                          size="sm"
                          fullWidth
                          gradient
                          onClick={(e) => {
                            e.preventDefault();
                            window.location.href = `/clients/${client.id}`;
                          }}
                          startDecorator={
                            <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                              visibility
                            </span>
                          }
                        >
                          View Details
                        </EnhancedButton>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            // Table view (simplified for now, can be enhanced further)
            <Card sx={{ overflow: 'hidden' }}>
              <Box sx={{ overflowX: 'auto' }}>
                {/* Enhanced table implementation would go here */}
                <Typography level="body-sm" sx={{ p: 3, textAlign: 'center' }}>
                  Table view coming soon - currently showing card view
                </Typography>
              </Box>
            </Card>
          )}

          {filteredClients.length === 0 && (
            <Card sx={{ p: 6, textAlign: 'center' }}>
              <span 
                className="material-symbols-outlined" 
                style={{ 
                  fontSize: '64px', 
                  color: '#6c757d',
                  fontFamily: 'Material Symbols Outlined'
                }}
              >
                business
              </span>
              <Typography level="h4" sx={{ mt: 2, mb: 2, color: 'neutral.500' }}>
                {search ? 'No Clients Found' : 'No Clients Yet'}
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                {search 
                  ? `No clients match your search for "${search}"`
                  : 'Clients will appear here once they are added to the system'
                }
              </Typography>
            </Card>
          )}
        </Box>
      </Box>
    </PageLayout>
  );
};

export default Clients;